import { useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { ConnectionStatusV2, IntegrationTypeV2 } from '@/gql/graphql';
import { MetaTrackingDetails } from './destinations/meta/meta-tracking-details';
import { PostscriptTrackingDetails } from './destinations/postscript/postscript-tracking-details';
import { KlaviyoTrackingDetails } from './destinations/klaviyo/klaviyo-tracking-details';
import { SnapchatTrackingDetails } from './destinations/snapchat/snapchat-tracking-details';
import { TiktokTrackingDetails } from './destinations/tiktok/tiktok-tracking-details';
import { SendlaneTrackingDetails } from './destinations/sendlane/sendlane-tracking-details';
import { AttentiveTrackingDetails } from './destinations/attentive/attentive-tracking-details';
import { RedoTrackingDetails } from './destinations/redo/redo-tracking-details';
import { WebhookTrackingDetails } from './destinations/webhook/webhook-tracking-details';
import { MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { Logo } from '@/components/logos/logo';
import { LOGO_MAPPING } from './tracking-utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TetrisSpinner } from '@/components/ui/tetris-spinner';
import { AppActions, AppTitle } from '@/components/app-header/app-header';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage
} from '@/components/ui/breadcrumb';
import { GoogleAdsCredentials } from './destinations/google-ads/googleads-settings';
import { INTEGRATIONS_IN_BETA } from '@/constants';
import { Badge } from '@/components/badge';
import { useIntegrationStore } from './stores/tracking-store';

// Define an interface for the expected location state
interface LocationState {
  isSetup?: boolean;
}

// Define props for the component
interface TrackingDetailsPageProps {
  isSetup?: boolean; // Optional prop, defaults to false in function signature
}

export function TrackingDetailsPage({ isSetup: propIsSetup = false }: TrackingDetailsPageProps) {
  // Added props and default
  const params = useParams<{ id: string; tab: string }>();
  const client = useApolloClient();
  const {
    fetchIntegration,
    integration,
    isIntegrationFetchLoading,
    deleteIntegration,
    updateIntegrationStatus
  } = useIntegrationStore();

  const navigate = useNavigate();
  const location = useLocation() as { state: LocationState | null }; // Use LocationState

  // Determine the final isSetup value: use location.state if available, otherwise use prop.
  const isSetup = location.state?.isSetup ?? propIsSetup;

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    action: 'publish' | 'delete' | 'deactivate' | null;
    title: string;
    description: string;
  }>({
    isOpen: false,
    action: null,
    title: '',
    description: ''
  });

  useEffect(() => {
    if (params.id) {
      void fetchIntegration(client, params.id);
    }
  }, [params.id, fetchIntegration, client]);

  useEffect(() => {
    // This is a workaround to ensure that the body pointer-events is set to 'auto'
    // after the modal is closed. This is necessary due to a bug in Radix's implementation
    // of the Dialog component where it always sets pointer-events to 'none' when a modal is open.

    // Note this isn't the same exact scenario (e.g. 3rd party dialog ontop of our dialog) but the issue
    // is the same - pointer-events keep getting set to 'none'.

    // The real solution here would be to better understand why the pointer-events are being set to 'none'
    // which is likely due to another underlying layer but this is a quick fix for now and we will revisit.
    // Github Issue Link (new): https://github.com/radix-ui/primitives/issues/1241
    // Original Issue: https://github.com/radix-ui/primitives/issues/2122
    const timer = setTimeout(() => {
      document.body.style.setProperty('pointer-events', 'auto', 'important');
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [modalState.isOpen]);

  const typedIntegration = integration;

  const integrationType = typedIntegration?.type;
  const isConnected = typedIntegration?.status === ConnectionStatusV2.Connected;

  const isPublishable = useMemo((): boolean => {
    if (!typedIntegration) return false;

    // Assert credentials and metadata as potentially having structure
    // Use Record<string, unknown> as a base type assertion for objects with unknown keys
    const credentials = typedIntegration.credentials as Record<string, unknown> | null | undefined;
    const metadata = typedIntegration.metadata as Record<string, unknown> | null | undefined;

    // Check if credentials object itself exists and has values
    const hasValidCredentials = credentials ? Object.values(credentials).every(Boolean) : false;

    if (
      typedIntegration.type === IntegrationTypeV2.Meta ||
      typedIntegration.type === IntegrationTypeV2.Ga4
    ) {
      // Check if metadata object exists and has values
      const hasValidMetadata = metadata ? Object.values(metadata).every(Boolean) : false;
      return hasValidCredentials && hasValidMetadata;
    }

    if (typedIntegration.type === IntegrationTypeV2.Googleads) {
      // Assert specific credential type for Google Ads
      const hasCustomerId = Boolean((credentials as GoogleAdsCredentials | undefined)?.customerId);

      const requiredMetadataFields = [
        'contentType',
        'productIdentifier',
        'googleAdsCtids'
      ] as const;
      const hasRequiredMetadata = metadata
        ? requiredMetadataFields.every((field) =>
            // Access property on asserted metadata object
            Boolean(metadata[field])
          )
        : false;

      return hasCustomerId && hasRequiredMetadata;
    }

    return hasValidCredentials;
  }, [typedIntegration]);

  const handleRemoveDestination = () => {
    if (!typedIntegration) return;
    deleteIntegration(client, params.id!)
      .then(() => {
        void navigate('..');
        toast({
          title: 'Success',
          description: 'Integration removed successfully.',
          variant: 'default'
        });
      })
      .catch((e: unknown) => {
        toast({
          title: 'Error',
          description: (e as Error).message,
          variant: 'destructive'
        });
      });
  };

  const handlePublish = () => {
    if (!isPublishable || !typedIntegration) return;

    updateIntegrationStatus(client, {
      id: typedIntegration.id,
      status: ConnectionStatusV2.Connected
    })
      .then(() => {
        toast({
          title: 'Success',
          description: 'Integration published successfully.',
          variant: 'default'
        });
      })
      .catch((e: unknown) => {
        toast({
          title: 'Error',
          description: (e as Error).message,
          variant: 'destructive'
        });
      });
  };

  const handleDeactivateDestination = () => {
    if (!typedIntegration) return;
    updateIntegrationStatus(client, {
      id: typedIntegration.id,
      status: ConnectionStatusV2.Disconnected
    })
      .then(() => {
        toast({
          title: 'Success',
          description: 'Integration deactivated successfully.',
          variant: 'default'
        });
      })
      .catch((e: unknown) => {
        toast({
          title: 'Error',
          description: (e as Error).message,
          variant: 'destructive'
        });
      });
  };

  const openModal = (action: 'publish' | 'delete' | 'deactivate') => {
    const modalContent = {
      publish: {
        title: 'Publish Destination',
        description: 'Are you sure you want to publish this destination?'
      },
      delete: {
        title: 'Delete Destination',
        description: 'Are you sure you want to delete this destination?'
      },
      deactivate: {
        title: 'Deactivate Destination',
        description: 'Are you sure you want to deactivate this destination?'
      }
    };

    setModalState({
      isOpen: true,
      action,
      title: modalContent[action].title,
      description: modalContent[action].description
    });
  };

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleModalConfirm = () => {
    switch (modalState.action) {
      case 'publish':
        handlePublish();
        break;
      case 'delete':
        handleRemoveDestination();
        break;
      case 'deactivate':
        handleDeactivateDestination();
        break;
    }
    closeModal();
  };

  const renderTrackingDetailsPage = () => {
    if (!integrationType || !integration) return null;

    switch (integrationType) {
      case IntegrationTypeV2.Meta:
        return <MetaTrackingDetails isPublishable={isPublishable} isSetup={isSetup} />;
      case IntegrationTypeV2.Postscript:
        return <PostscriptTrackingDetails isSetup={isSetup} />;
      case IntegrationTypeV2.Klaviyo:
        return <KlaviyoTrackingDetails isSetup={isSetup} />;
      case IntegrationTypeV2.Snapchat:
        return <SnapchatTrackingDetails isPublishable={isPublishable} isSetup={isSetup} />;
      case IntegrationTypeV2.Tiktok:
        return <TiktokTrackingDetails isPublishable={isPublishable} isSetup={isSetup} />;
      case IntegrationTypeV2.Sendlane:
        return <SendlaneTrackingDetails isSetup={isSetup} />;
      case IntegrationTypeV2.Attentive:
        return <AttentiveTrackingDetails isSetup={isSetup} />;
      case IntegrationTypeV2.Redo:
        return <RedoTrackingDetails isSetup={isSetup} />;
      case IntegrationTypeV2.Webhook:
        return <WebhookTrackingDetails isSetup={isSetup} />;
      default:
        return null;
    }
  };

  if (isIntegrationFetchLoading) {
    return (
      <div>
        <TetrisSpinner width={20} />
      </div>
    );
  }

  return (
    <div>
      <AppTitle>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink className="text-base" asChild>
                <Link to="../../tracking">Tracking</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-base">{typedIntegration?.name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </AppTitle>

      <AppActions>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  onClick={() => {
                    openModal('publish');
                  }}
                  disabled={!isPublishable || isConnected}
                >
                  Publish
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {!isPublishable
                ? 'Fill out and save all pages before publishing'
                : isConnected
                  ? 'Integration is already published'
                  : 'Publish this integration'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </AppActions>

      <AppActions>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="rounded-lg" align="end">
              <DropdownMenuItem
                onClick={() => {
                  openModal('deactivate');
                }}
                disabled={!isConnected}
                className={!isConnected ? 'cursor-not-allowed opacity-50 rounded-lg' : 'rounded-lg'}
              >
                Deactivate Destination
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  openModal('delete');
                }}
                disabled={isConnected}
                className={isConnected ? 'cursor-not-allowed opacity-50 rounded-lg' : 'rounded-lg'}
              >
                Remove Destination
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </AppActions>
      <div className="max-w-[1024px] mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            {typedIntegration?.type && typedIntegration.type !== IntegrationTypeV2.Unspecified && (
              <Logo icon={LOGO_MAPPING[typedIntegration.type]} />
            )}
            <h1 className="text-3xl font-medium">
              {typedIntegration?.name ?? 'Integration Details'}
            </h1>
            {typedIntegration?.type && INTEGRATIONS_IN_BETA.includes(typedIntegration.type) && (
              <Badge>Beta</Badge>
            )}
          </div>
        </div>

        {renderTrackingDetailsPage()}
        <Dialog
          open={modalState.isOpen}
          onOpenChange={(open) => {
            setModalState((prev) => ({ ...prev, isOpen: open }));
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{modalState.title}</DialogTitle>
              <DialogDescription>{modalState.description}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button onClick={handleModalConfirm}>
                {modalState.action === 'delete' ? 'Remove' : 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
